import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import Cookies from "js-cookie";
import axios from "axios";
import {
  UploadCloud,
  FileSpreadsheet,
  Loader2,
  DollarSign,
  Trash2,
  ArrowRight,
  Settings,
  LogOut,
  HelpCircle,
  ChevronRight,
  X,
  FolderOpen,
  Clock,
  BarChart3,
  MapPin,
  CheckCircle,
  FileText,
  Radio,
  ChevronDown,
  Megaphone,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";
import { ShiftReview } from "./ShiftReview";
import { SettingsPanel } from "./SettingsPanel";
import { PaywallModal } from "./PaywallModal";
import { PastReports } from "./PastReports";
import { LaborCostTab } from "./LaborCostTab";
import { ComparisonDashboard } from "./ComparisonDashboard";
import { AdminDashboard } from "./AdminDashboard";
import { CloverBridge } from "./CloverBridge";
import { WeddingTracker } from "./WeddingTracker";
import { analytics } from "./analytics";
import { API } from "./apiConfig";

const USAGE_COOKIE = "oly_payroll_runs";

function getUsageCount() {
  return parseInt(Cookies.get(USAGE_COOKIE) || "0", 10);
}
function incrementUsage() {
  const current = getUsageCount();
  Cookies.set(USAGE_COOKIE, String(current + 1), { expires: 365 });
  return current + 1;
}
function hasFreeExportClaim() {
  return Cookies.get("oly_free_claimed") === "1";
}
function setFreeExportClaimed() {
  Cookies.set("oly_free_claimed", "1", { expires: 365 });
}

// Tutorial steps
const TUTORIAL_STEPS = [
  {
    title: "Upload Your CSV",
    description: "Export shifts from your Clover POS app and drag the CSV file into the upload area below.",
    target: "upload-dropzone",
  },
  {
    title: "Enter Store Tips",
    description: "Type in the total tips for this pay period. This is used to calculate tip distribution.",
    target: "store-tips-input",
  },
  {
    title: "Process & Review",
    description: "Click Process Shifts. You'll see a detailed breakdown with overtime, anomalies, and more.",
    target: "process-btn",
  },
];

export default function ToolPage() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [storeTips, setStoreTips] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallStats, setPaywallStats] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(-1);
  const [showTimeSaved, setShowTimeSaved] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [weddingTrackerOpen, setWeddingTrackerOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateLoading, setGateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("shifts");
  const [stateCode, setStateCode] = useState("CA");
  const [stateRules, setStateRules] = useState({});
  const [breakThreshold, setBreakThreshold] = useState(5);
  const [payFrequency, setPayFrequency] = useState("weekly");
  const [isSampleData, setIsSampleData] = useState(false);
  const [cloverSyncing, setCloverSyncing] = useState(false);
  const [cloverPanelOpen, setCloverPanelOpen] = useState(false);
  const [cloverStartDate, setCloverStartDate] = useState("");
  const [cloverEndDate, setCloverEndDate] = useState("");

  // Load state rules on mount
  useEffect(() => {
    axios.get(`${API}/state-rules`).then((r) => setStateRules(r.data)).catch(() => {});
  }, []);

  // Check if user has seen tutorial
  useEffect(() => {
    const seen = Cookies.get("oly_tutorial_done");
    if (!seen) {
      setTutorialStep(0);
    }
  }, []);

  const refreshSettings = useCallback(() => {
    axios.get(`${API}/settings`, { withCredentials: true }).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const canAccessPremium = user && (user.subscription_status === "unlimited" || user.role === "admin");
  const hasExportCredits = user && (user.export_credits || 0) > 0;

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const f = e.dataTransfer.files[0];
      if (f.name.toLowerCase().endsWith(".csv")) {
        setFile(f);
        setIsSampleData(false);
        analytics.csvUploaded(f.name);
        toast.success("File uploaded", { description: f.name });
      } else {
        toast.error("Invalid file", { description: "Please upload a .csv file" });
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setIsSampleData(false);
      analytics.csvUploaded(e.target.files[0].name);
      toast.success("File uploaded", { description: e.target.files[0].name });
    }
  };

  const handleLoadSample = async () => {
    try {
      const response = await fetch("/sample-shifts.csv");
      const blob = await response.blob();
      const sampleFile = new File([blob], "sample-shifts.csv", { type: "text/csv" });
      setFile(sampleFile);
      setStoreTips("350");
      setIsSampleData(true);
      analytics.sampleDataUsed();
      toast.success("Sample data loaded", { description: "4 employees, 20 shifts — ready to process" });
    } catch {
      toast.error("Failed to load sample data");
    }
  };

  const handleCloverSync = async () => {
    if (!cloverStartDate || !cloverEndDate) {
      toast.error("Select dates", { description: "Pick the pay period start and end dates." });
      return;
    }
    if (!storeTips && storeTips !== "0") {
      toast.error("Enter store tips", { description: "Enter the total store tips for this pay period (or 0)." });
      return;
    }
    setCloverSyncing(true);
    try {
      const { data } = await axios.post(`${API}/clover/sync-shifts`, {
        start_date: cloverStartDate,
        end_date: cloverEndDate,
      }, { withCredentials: true });

      if (data.total_pulled === 0) {
        toast.info("No shifts found", { description: "No shifts in the selected date range." });
      } else {
        toast.success(`Synced ${data.total_pulled} shifts`, {
          description: `${data.inserted} new, ${data.updated} updated. Tips: $${storeTips || "0"}`,
        });
      }
      setCloverPanelOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error("Clover sync failed", { description: detail });
    } finally {
      setCloverSyncing(false);
    }
  };

  // Helper: get last Sun-Sat range
  const setLastWeekRange = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    // Last Saturday: if today is Sunday(0), last Sat was yesterday; otherwise go back (day+1) days
    const daysToLastSat = day === 0 ? 1 : day + 1;
    const lastSat = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToLastSat);
    const lastSun = new Date(lastSat.getFullYear(), lastSat.getMonth(), lastSat.getDate() - 6);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setCloverStartDate(fmt(lastSun));
    setCloverEndDate(fmt(lastSat));
  };

  const handleProcess = async () => {
    if (!file) {
      toast.error("No file selected", { description: "Upload a CSV file first" });
      return;
    }

    // Paid users (unlimited subscription or has export credits) always pass through
    const isPaid = canAccessPremium || hasExportCredits;

    if (!isPaid && !user) {
      // Anonymous user — need email gate + server-side IP check
      if (!hasFreeExportClaim()) {
        // Check server-side if this IP already used a free export
        try {
          const { data: check } = await axios.post(`${API}/check-free-export`, {}, { withCredentials: true });
          if (!check.allowed) {
            toast.info("Create an account to continue", {
              description: check.message || "Your free payroll run has been used.",
            });
            navigate("/auth?mode=register");
            return;
          }
        } catch {
          // If check fails, fall through to email gate
        }
        // Show email capture modal
        setEmailGateOpen(true);
        return;
      }
    } else if (!isPaid && user) {
      const usageCount = user.exports_used || 0;
      // 2nd+ run with free account: require payment
      if (usageCount >= 2) {
        analytics.paywallShown("payment_required");
        setPaywallStats(null);
        setPaywallOpen(true);
        return;
      }
    }

    await _doProcess();
  };

  const _doProcess = async () => {
    analytics.processingStarted(stateCode, payFrequency);
    setProcessing(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("store_tips", storeTips || "0");
    formData.append("state_code", stateCode);
    formData.append("pay_frequency", payFrequency);
    formData.append("is_sample", isSampleData ? "true" : "false");

    try {
      const response = await axios.post(`${API}/process-shifts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      setResult(response.data);
      if (response.data.break_threshold != null) setBreakThreshold(response.data.break_threshold);
      incrementUsage();
      analytics.processingCompleted(
        response.data.total_shifts_processed,
        response.data.shifts ? new Set(response.data.shifts.map(s => s.emp_name)).size : 0
      );
      setShowTimeSaved(true);
      setTimeout(() => setShowTimeSaved(false), 8000);
      toast.success("Processing complete!", {
        description: `${response.data.total_shifts_processed} shifts processed`,
      });
      // Refresh auth state to sync export credits/usage
      if (user) checkAuth();
      // Refresh settings to pick up auto-added tip employees
      refreshSettings();
    } catch (err) {
      if (err.response?.status === 402) {
        // Server-side paywall enforcement
        if (!user) {
          toast.info("Create an account to continue", {
            description: err.response?.data?.detail || "Sign up to keep using Oly Payroll.",
          });
          navigate("/auth?mode=register");
        } else {
          setPaywallStats(null);
          setPaywallOpen(true);
        }
      } else {
        const msg = err.response?.data?.detail || "An error occurred during processing";
        toast.error("Processing failed", { description: msg });
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleEmailGateSubmit = async () => {
    if (!gateEmail.trim() || !gateEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setGateLoading(true);
    try {
      await axios.post(`${API}/claim-free-export`, { email: gateEmail }, { withCredentials: true });
      setFreeExportClaimed();
      setEmailGateOpen(false);
      toast.success("Free export unlocked!", { description: "Processing your payroll now..." });
      await _doProcess();
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes("already used")) {
        toast.info("Create an account to continue", {
          description: "Your free export has already been used from this device.",
        });
        navigate("/auth?mode=register");
        setEmailGateOpen(false);
      } else {
        toast.error(err.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setGateLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result?.file_id) return;

    // If user already processed shifts in this session, they've passed the gate.
    // Credits were consumed at process time — don't re-gate the download.
    const link = document.createElement("a");
    link.href = `${API}/download/${result.file_id}`;
    link.download = result.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    analytics.reportDownloaded(result.file_name);
    toast.success("Download started", { description: result.file_name });
  };

  const handleReset = () => {
    setFile(null);
    setStoreTips("");
    setResult(null);
    setShowTimeSaved(false);
    setIsSampleData(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSettingsClick = () => {
    if (!canAccessPremium) {
      toast.info("Premium feature", {
        description: "Settings is available on the Unlimited plan.",
      });
      setPaywallOpen(true);
      return;
    }
    setSettingsOpen(true);
  };

  const dismissTutorial = () => {
    setTutorialStep(-1);
    Cookies.set("oly_tutorial_done", "1", { expires: 365 });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tool-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="tool-header">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-heading text-xl font-light text-[#0A1F3B]">oly</span>
            <span className="font-heading text-xl font-black text-[#0A1F3B]">Payroll</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTutorialStep(0);
              }}
              className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
              data-testid="help-btn"
            >
              <HelpCircle size={16} />
              <span className="hidden sm:inline ml-1">Help</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSettingsClick}
              className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
              data-testid="settings-btn"
            >
              <Settings size={16} />
              <span className="hidden sm:inline ml-1">Settings</span>
            </Button>
            {canAccessPremium && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReportsOpen(true)}
                className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
                data-testid="reports-btn"
              >
                <FolderOpen size={16} />
                <span className="hidden sm:inline ml-1">Reports</span>
              </Button>
            )}
            {user?.role === "admin" && (
              <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAdminOpen(true)}
                className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
                data-testid="admin-btn"
              >
                <BarChart3 size={16} />
                <span className="hidden sm:inline ml-1">Admin</span>
              </Button>
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setToolsDropdownOpen(!toolsDropdownOpen)}
                  className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
                  data-testid="tools-dropdown-btn"
                >
                  <Settings size={16} />
                  <span className="hidden sm:inline ml-1">Tools</span>
                  <ChevronDown size={14} className={`ml-0.5 transition-transform ${toolsDropdownOpen ? "rotate-180" : ""}`} />
                </Button>
                {toolsDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setToolsDropdownOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-testid="tools-dropdown-menu">
                      <button
                        onClick={() => { setBridgeOpen(true); setToolsDropdownOpen(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        data-testid="tools-bridge-btn"
                      >
                        <Radio size={15} /> Clover Bridge
                      </button>
                      <button
                        onClick={() => { setWeddingTrackerOpen(true); setToolsDropdownOpen(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        data-testid="tools-wedding-btn"
                      >
                        <FileSpreadsheet size={15} /> Wedding Tracker
                      </button>
                      <button
                        onClick={() => { navigate("/admin/marketing"); setToolsDropdownOpen(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        data-testid="tools-marketing-btn"
                      >
                        <Megaphone size={15} /> Marketing Engine
                      </button>
                    </div>
                  </>
                )}
              </div>
              </>
            )}
            {user ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600 hidden sm:inline">{user.email}</span>
                {user.subscription_status === "unlimited" && (
                  <span className="text-[10px] bg-[#0A1F3B]/10 text-[#0A1F3B] px-2 py-0.5 rounded-full font-bold">PRO</span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="text-slate-600 hover:text-[#0A1F3B] hover:bg-slate-200"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} />
                </Button>
              </div>
            ) : (
              <Link
                to="/auth"
                className="text-sm font-medium text-[#0A1F3B] hover:underline"
                data-testid="signin-link"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSave={(s) => { setSettings(s); setSettingsOpen(false); }}
      />

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        stats={paywallStats}
      />

      {/* Email gate modal for free export */}
      {emailGateOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-gate-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5D300] rounded-full mb-3">
                <FileSpreadsheet className="w-6 h-6 text-[#0A1F3B]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F3B]">Your first payroll run is free</h3>
              <p className="text-sm text-gray-600 mt-1">Enter your email to unlock your free export. No account needed.</p>
            </div>
            <input
              type="email"
              value={gateEmail}
              onChange={(e) => setGateEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEmailGateSubmit()}
              placeholder="you@business.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F5D300] focus:border-transparent"
              data-testid="email-gate-input"
              autoFocus
            />
            <button
              onClick={handleEmailGateSubmit}
              disabled={gateLoading}
              className="w-full mt-3 bg-[#0A1F3B] text-white font-bold rounded-lg py-3 text-sm hover:bg-[#0A1F3B]/90 disabled:opacity-50 transition-all"
              data-testid="email-gate-submit"
            >
              {gateLoading ? "Unlocking..." : "Unlock Free Export"}
            </button>
            <p className="text-[10px] text-gray-400 mt-3 text-center leading-relaxed">
              We'll send you payroll tips (you can unsubscribe anytime). No spam, ever.
              By continuing you agree to our <a href="/terms" className="underline">Terms</a> and <a href="/privacy" className="underline">Privacy Policy</a>.
            </p>
            <button
              onClick={() => setEmailGateOpen(false)}
              className="w-full mt-2 text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <PastReports
        isOpen={reportsOpen}
        onClose={() => setReportsOpen(false)}
        timezone={settings?.timezone || "America/Los_Angeles"}
      />

      <AdminDashboard
        isOpen={adminOpen}
        onClose={() => setAdminOpen(false)}
      />

      <CloverBridge
        isOpen={bridgeOpen}
        onClose={() => setBridgeOpen(false)}
      />

      <WeddingTracker
        isOpen={weddingTrackerOpen}
        onClose={() => setWeddingTrackerOpen(false)}
      />

      {/* Tutorial Overlay */}
      {tutorialStep >= 0 && tutorialStep < TUTORIAL_STEPS.length && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6" data-testid="tutorial-overlay">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-sm w-full shadow-[0_0_40px_-12px_rgba(245,211,0,0.1)]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#0A1F3B]">
                Step {tutorialStep + 1} of {TUTORIAL_STEPS.length}
              </span>
              <button onClick={dismissTutorial} className="text-slate-500 hover:text-[#0A1F3B] transition-colors">
                <X size={16} />
              </button>
            </div>
            <h3 className="font-heading text-lg font-bold text-[#0A1F3B] mb-2">
              {TUTORIAL_STEPS[tutorialStep].title}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              {TUTORIAL_STEPS[tutorialStep].description}
            </p>
            <div className="flex items-center justify-between">
              {tutorialStep > 0 && (
                <button
                  onClick={() => setTutorialStep((s) => s - 1)}
                  className="text-sm text-slate-600 hover:text-[#0A1F3B] transition-colors"
                >
                  Back
                </button>
              )}
              <div className="ml-auto">
                {tutorialStep < TUTORIAL_STEPS.length - 1 ? (
                  <button
                    onClick={() => setTutorialStep((s) => s + 1)}
                    className="inline-flex items-center gap-1 bg-[#0A1F3B] text-white font-bold text-sm rounded-lg px-4 py-2 hover:-translate-y-0.5 transition-all"
                    data-testid="tutorial-next-btn"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={dismissTutorial}
                    className="inline-flex items-center gap-1 bg-[#0A1F3B] text-white font-bold text-sm rounded-lg px-4 py-2 hover:-translate-y-0.5 transition-all"
                    data-testid="tutorial-done-btn"
                  >
                    Got it!
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Saved Banner */}
      {showTimeSaved && (
        <div className="bg-[#0A1F3B]/5 border-b border-slate-200 py-3 px-6 text-center animate-in slide-in-from-top" data-testid="time-saved-banner">
          <p className="text-sm text-[#0A1F3B] font-semibold">
            This would normally take ~45 minutes. You did it in seconds.
          </p>
        </div>
      )}

      <main className="px-6 py-8">
        <div className={`max-w-7xl mx-auto ${!result ? "max-w-2xl" : ""}`}>
          {/* Upload Section */}
          {!result && (
            <section className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="upload-section">
              {/* No-signup trust badge */}
              {!user && (
                <div className="mb-6 inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5" data-testid="free-trial-badge">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="text-green-800 text-xs font-bold">Your first payroll run is free — just enter your email</span>
                </div>
              )}

              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1F3B] mb-2">Step 1</p>
                <h2 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-1">Upload Shift Export</h2>
                <p className="text-sm text-slate-600">
                  Upload the raw CSV file exported from your Clover POS Shifts app.
                </p>
              </div>

              <div
                data-testid="upload-dropzone"
                className={`border-2 border-dashed rounded-2xl p-12 cursor-pointer transition-all duration-300 text-center ${
                  dragActive
                    ? "border-[#0A1F3B] bg-[#0A1F3B]/5"
                    : file
                    ? "border-green-500/50 bg-green-500/5"
                    : "border-slate-200 bg-slate-50 hover:bg-[#0A1F3B]/5 hover:border-[#0A1F3B]/50"
                }`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileSpreadsheet className="w-9 h-9 text-green-700" />
                    <div className="text-left">
                      <p className="text-[#0A1F3B] font-semibold" data-testid="uploaded-filename">{file.name}</p>
                      <p className="text-xs text-slate-600">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      data-testid="remove-file-btn"
                      className="ml-4 p-2 rounded-lg text-slate-600 hover:text-red-700 hover:bg-red-500/10 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <UploadCloud className="w-10 h-10 text-slate-500" />
                    <p className="text-sm text-slate-600">
                      Drop your CSV file here, or{" "}
                      <span className="text-[#0A1F3B] font-semibold">browse</span>
                    </p>
                    <p className="text-xs text-slate-500">Accepts .csv files from Clover POS</p>
                  </div>
                )}
              </div>

              {/* Sample data button */}
              {!file && (
                <div className="mt-3 text-center" data-testid="sample-data-section">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLoadSample(); }}
                    className="inline-flex items-center gap-1.5 text-sm text-[#0A1F3B] font-semibold hover:underline transition-colors"
                    data-testid="sample-data-btn"
                  >
                    <FileText size={14} />
                    Don&apos;t have a CSV? Try with sample data
                  </button>
                  <p className="text-[11px] text-slate-400 mt-1">Loads 4 employees, 20 shifts, and $350 in tips</p>
                </div>
              )}

              {/* Clover Sync */}
              {user?.clover_merchant_id && !file && (
                <div className="mt-5" data-testid="clover-sync-section">
                  {!cloverPanelOpen ? (
                    <div className="text-center">
                      <button
                        onClick={() => { setCloverPanelOpen(true); setLastWeekRange(); }}
                        className="inline-flex items-center gap-2 bg-[#0A1F3B] text-white font-semibold rounded-lg px-5 py-2.5 text-sm hover:bg-[#0A1F3B]/90 transition-colors"
                        data-testid="clover-sync-open-btn"
                      >
                        <ArrowRight size={14} />
                        Sync Shifts from Clover
                      </button>
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm" data-testid="clover-sync-panel">
                      <div className="flex items-center justify-between">
                        <h3 className="font-heading text-base font-bold text-[#0A1F3B]">Sync from Clover</h3>
                        <button onClick={() => setCloverPanelOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="clover-sync-close">
                          <X size={16} />
                        </button>
                      </div>

                      {/* Date Range */}
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-[#0A1F3B] mb-1.5">Pay Period</label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Input
                            type="date"
                            value={cloverStartDate}
                            onChange={(e) => setCloverStartDate(e.target.value)}
                            className="h-9 text-sm text-[#0A1F3B] w-40"
                            data-testid="clover-start-date"
                          />
                          <span className="text-xs text-slate-500">to</span>
                          <Input
                            type="date"
                            value={cloverEndDate}
                            onChange={(e) => setCloverEndDate(e.target.value)}
                            className="h-9 text-sm text-[#0A1F3B] w-40"
                            data-testid="clover-end-date"
                          />
                          <button
                            onClick={setLastWeekRange}
                            className="text-xs text-[#0A1F3B] font-semibold hover:underline whitespace-nowrap"
                            data-testid="clover-last-week-btn"
                          >
                            Last Sun–Sat
                          </button>
                        </div>
                      </div>

                      {/* Store Tips */}
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-[#0A1F3B] mb-1.5">Store Tips</label>
                        <p className="text-xs text-slate-600 mb-2">Total store tips for this pay period.</p>
                        <div className="relative max-w-xs">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1F3B] pointer-events-none" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={storeTips}
                            onChange={(e) => setStoreTips(e.target.value)}
                            className="pl-9 h-9 text-sm text-[#0A1F3B]"
                            data-testid="clover-tips-input"
                          />
                        </div>
                      </div>

                      {/* Summary + Confirm */}
                      <div className="flex items-center gap-3 pt-1">
                        <Button
                          onClick={handleCloverSync}
                          disabled={cloverSyncing}
                          className="bg-[#0A1F3B] text-white hover:bg-[#0A1F3B]/90 gap-2"
                          data-testid="clover-sync-confirm-btn"
                        >
                          {cloverSyncing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                          {cloverSyncing ? "Syncing..." : "Confirm & Sync"}
                        </Button>
                        {cloverStartDate && cloverEndDate && (
                          <span className="text-xs text-slate-600">
                            {cloverStartDate} to {cloverEndDate}
                            {storeTips ? ` · $${storeTips} tips` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {user && !user.clover_merchant_id && !file && (
                <p className="text-[10px] text-slate-400 text-center mt-3">
                  Or connect your Clover account by installing &ldquo;oly Payroll Prep&rdquo; from your Clover App Market.
                </p>
              )}

              {/* Step 2: Store Tips — hidden when Clover sync panel is open (tips are in the panel) */}
              {!(user?.clover_merchant_id && cloverPanelOpen) && (
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1F3B] mb-2">Step 2</p>
                <h2 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-1">Enter Store Tips</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Enter the total store tips for this pay period.
                </p>
                <div className="relative max-w-xs">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 pointer-events-none" />
                  <Input
                    data-testid="store-tips-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={storeTips}
                    onChange={(e) => setStoreTips(e.target.value)}
                    className="pl-8 h-11 bg-white border-slate-200 text-[#0A1F3B] font-mono placeholder-slate-400 focus:border-[#0A1F3B] focus:ring-[#0A1F3B]"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500 max-w-md leading-relaxed" data-testid="tips-explanation">
                  This total is split among all <span className="font-semibold text-[#0A1F3B]">tip-eligible employees</span> by dividing the amount by
                  their total shifts worked. Manage who receives tips in Settings.
                </p>
              </div>
              )}

              {/* Pay Frequency */}
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1F3B] mb-2">Step 3</p>
                <h2 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-1">Pay Frequency</h2>
                <p className="text-sm text-slate-600 mb-4">
                  How often do you run payroll? This affects overtime calculations.
                </p>
                <div className="flex gap-2 max-w-md" data-testid="pay-frequency-selector">
                  {[
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Bi-weekly" },
                    { value: "monthly", label: "Monthly" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      data-testid={`freq-${opt.value}`}
                      onClick={() => setPayFrequency(opt.value)}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border transition-all duration-200 ${
                        payFrequency === opt.value
                          ? "bg-[#0A1F3B] text-white border-[#0A1F3B]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#0A1F3B]/40"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* State Selector */}
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A1F3B] mb-2">Step 4</p>
                <h2 className="font-heading text-2xl font-bold text-[#0A1F3B] mb-1">Select State</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Break and overtime rules vary by state. Select your state for accurate compliance checks.
                </p>
                <div className="relative max-w-xs">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 pointer-events-none" />
                  <select
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}
                    className="w-full h-11 pl-8 pr-3 bg-white border border-slate-200 text-[#0A1F3B] text-sm rounded-md focus:border-[#0A1F3B] focus:outline-none focus:ring-1 focus:ring-[#0A1F3B] cursor-pointer appearance-none"
                    data-testid="state-selector"
                  >
                    {Object.values(stateRules).length > 0
                      ? Object.values(stateRules)
                          .sort((a, b) => a.state.localeCompare(b.state))
                          .map((s) => (
                            <option key={s.abbr} value={s.abbr}>
                              {s.state} ({s.abbr})
                            </option>
                          ))
                      : <option value="CA">California (CA)</option>
                    }
                  </select>
                </div>
                {stateRules[stateCode] && (
                  <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg max-w-md" data-testid="state-rules-note">
                    <p className="text-[10px] text-slate-600">
                      {stateRules[stateCode].has_meal_break ? (
                        <span>
                          <span className="text-[#0A1F3B] font-semibold">Meal break</span>: Required after {stateRules[stateCode].meal_threshold}h ({stateRules[stateCode].meal_duration}min)
                          {stateRules[stateCode].waiver_max && ` — waivable if shift ≤${stateRules[stateCode].waiver_max}h`}
                          {stateRules[stateCode].has_rest_break && (
                            <span className="ml-2">
                              | <span className="text-[#0A1F3B] font-semibold">Rest break</span>: {stateRules[stateCode].rest_duration}min per {stateRules[stateCode].rest_per_hours}h
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-500">{stateRules[stateCode].note}</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              <Button
                data-testid="process-btn"
                onClick={handleProcess}
                disabled={!file || processing}
                className="mt-8 h-12 px-8 bg-[#0A1F3B] text-white font-bold rounded-lg hover:-translate-y-1 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    Process Shifts
                    <ArrowRight size={18} />
                  </>
                )}
              </Button>
            </section>
          )}

          {/* Edit & Review Section */}
          {result && (
            <div>
              {/* Tab Navigation */}
              <div className="flex items-center gap-1 mb-4 border-b border-slate-200 pb-0" data-testid="tool-tabs">
                {[
                  { key: "shifts", label: "Shifts", icon: <Clock size={14} /> },
                  ...(canAccessPremium ? [
                    { key: "labor", label: "Labor Cost", icon: <DollarSign size={14} /> },
                    { key: "comparison", label: "Comparison", icon: <BarChart3 size={14} /> },
                  ] : []),
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors relative ${
                      activeTab === tab.key
                        ? "text-[#0A1F3B]"
                        : "text-slate-500 hover:text-[#0A1F3B]"
                    }`}
                    data-testid={`tab-${tab.key}`}
                  >
                    {tab.icon}
                    {tab.label}
                    {activeTab === tab.key && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0A1F3B] rounded-t" />
                    )}
                  </button>
                ))}
              </div>

              {activeTab === "shifts" && (
                <ShiftReview
                  data={result}
                  storeTips={storeTips}
                  onReset={() => { handleReset(); setActiveTab("shifts"); }}
                  settings={settings}
                  onUpdateShifts={(newShifts) => setResult((prev) => ({ ...prev, shifts: newShifts }))}
                  onDownload={handleDownload}
                  canAccessPremium={canAccessPremium}
                  breakThreshold={breakThreshold}
                  isSampleData={isSampleData}
                />
              )}

              {activeTab === "labor" && (
                <LaborCostTab
                  shifts={result.shifts}
                  settings={settings}
                />
              )}

              {activeTab === "comparison" && canAccessPremium && (
                <ComparisonDashboard />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
